import React, { useEffect, useState } from "react";
import ModalCustom from "../../../../components/modal/ModalCustom";
import { Grid } from "@mui/material";
import {
  ButtonOutlined,
  ButtonPrimary,
} from "../../../../components/button/ButtonCustoms";
import InputTextField from "../../../../components/input/inputTextField";
import UploadFile from "../../../../components/upload/UploadFile";
import { useDispatch } from "react-redux";
import {
  setLoading,
  unsetLoading,
} from "../../../../store/loading/loading.slice";
import {
  alertActive,
  alertError,
  alertSuccess,
} from "../../../../components/alert/swal";
import { advisorFileApi } from "../../../../api/advisor-file/advisor-file.api";
import { routeName } from "../../../../routes/routeName";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { truncate } from "fs/promises";
import { saveAs } from "file-saver";
import { getFileExtension } from "../../../../utils/fileTypeDownload";

type ModalFormDocumentProps = {
  isOpenModal: boolean;
  setIsOpenModal: (isOpen: boolean) => void;
  data?: any;
  action: string;
  onFetch?: () => void;
};

const ModalFormDocument: React.FC<ModalFormDocumentProps> = ({
  isOpenModal,
  setIsOpenModal,
  action,
  data,
  onFetch,
}) => {
  const { t } = useTranslation();
  const navigate: NavigateFunction = useNavigate();
  const dispatch = useDispatch();

  const [isCanEdit, setIsCanEdit] = useState<boolean>(false);
  const [handleDeleted, setHandleDeleted] = useState(false);
  const [file, setFile] = useState(null);

  const [formDocument, setFormDocument] = useState<{
    document_name: string;
  }>({
    document_name: "",
  });

  const [errorMessage, setErrorMessage] = useState<any[]>([]);

  const handleChangeValue = (name: string, value: any) => {
    setFormDocument((prev: any) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  useEffect(() => {
    if (action === "create") {
      setIsCanEdit(true);
      setHandleDeleted(false);
    } else {
      setIsCanEdit(false);
      setHandleDeleted(true);
    }
  }, [action]);

  useEffect(() => {
    if (data) {
      setFormDocument({
        document_name: data.advisor_file,
      });
    }
  }, [data]);

  const validateForm = () => {
    const errors: string[] = [];

    // Check if required fields are empty
    if (!formDocument.document_name) {
      errors.push("document_name");
    }

    if (!file) {
      errors.push("file");
    }

    setErrorMessage(errors);

    // If there are no errors, return true
    return errors.length === 0;
  };

  const handleSave = async (newFile?: File, advisor_file?: any) => {
    if (validateForm() && newFile && advisor_file) {
      const formData = new FormData();

      formData.append("file", newFile);
      formData.append("advisor_file", advisor_file);
      formData.append("status", "active");

      try {
        dispatch(unsetLoading());
        const response = await advisorFileApi.createAdvisorFile(formData);
        if (response.status < 300) {
          alertSuccess(
            "บันทึกเอกสาร",
            "บันทึกเอกสาร เรียบร้อยแล้ว",
            undefined,
            () => {
              onFetch && onFetch();
              setIsOpenModal(false);
              setFormDocument({
                document_name: "",
              });
              setHandleDeleted(true);
              setFile(null);
            }
          );
        }
      } catch (error) {
        alertError("บันทึกเอกสาร ไม่สำเร็จ");
      } finally {
        dispatch(unsetLoading());
      }
    }
  };

  const handleUpdate = async (
    id: number,
    newFile?: File,
    advisor_file?: any,
    status?: string
  ) => {
    if (id && advisor_file) {
      const formData = new FormData();

      if (newFile) {
        formData.append("file", newFile);
      }

      formData.append("advisor_file", advisor_file);
      formData.append("status", status ?? "active");

      try {
        dispatch(unsetLoading());
        const response = await advisorFileApi.updateAdvisorFile(id, formData);
        if (response.status < 300) {
          alertSuccess(
            "แก้ไขเอกสาร",
            "แก้ไขเอกสาร เรียบร้อยแล้ว",
            undefined,
            () => {
              onFetch && onFetch();
              setIsOpenModal(false);
              setIsCanEdit(false);
              setFormDocument({
                document_name: "",
              });
              setFile(null);
              setHandleDeleted(true);
            }
          );
        }
      } catch (error) {
        alertError("แก้ไขเอกสาร ไม่สำเร็จ");
      } finally {
        dispatch(unsetLoading());
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (id) {
      alertActive(
        "ลบเอกสาร",
        "ท่านต้องการลบเอกสาร ใช่หรือไม่",
        undefined,
        undefined,
        async () => {
          dispatch(setLoading());
          await advisorFileApi
            .deleteAdvisorFile(+id)
            .then((res) => {
              if (res.status < 300) {
                alertSuccess("ลบเอกสาร เรียบร้อย", undefined, undefined, () => {
                  onFetch && onFetch();
                  setIsOpenModal(false);
                  setIsCanEdit(false);
                  setHandleDeleted(true);
                  setFile(null);
                });
              }
            })
            .catch(() => {
              alertError("เอกสาร ไม่สำเร็จ");
            })
            .finally(() => {
              dispatch(unsetLoading());
            });
        }
      );
    }
    return;
  };

  const handleDownloadFile = async (id: number, name_file: string) => {
    dispatch(setLoading());
    await advisorFileApi
      .downloadFile(id)
      .then((res) => {
        if (res.status < 300) {
          const response = res.data;

          // Get the content type from the server's response headers
          const contentType = res.headers["content-type"];

          // Create a Blob with the content type from the response
          const blob = new Blob([response], { type: contentType });

          // Extract file extension based on content type
          const fileExtension = getFileExtension(contentType || "");

          // Generate a filename with the correct extension (you can also modify this to use a custom name)
          const fileName = `${name_file}.${fileExtension}`;

          // Save the file
          saveAs(blob, fileName);
        }
      })
      .catch((error) => console.log(error))
      .finally(() => dispatch(unsetLoading()));
  };

  console.log("data", data);
  return (
    <ModalCustom
      title={action === "create" ? "เพิ่มเพิ่มเอกสาร" : "แก้ไขเพิ่มเอกสาร"}
      subTitle="กรุณาระบุข้อมูลให้ครบถ้วนก่อนการยืนยัน"
      open={isOpenModal}
      onClose={() => {
        setIsOpenModal(false);
      }}
      component={
        <Grid container spacing={2}>
          <Grid
            item
            lg={12}
            md={12}
            sm={12}
            xs={12}
            sx={{
              alignSelf: "end",
            }}
          >
            <InputTextField
              heading={"ชื่อเอกสาร"}
              placeholder={"ชื่อเอกสาร"}
              value={formDocument.document_name ?? ""}
              onchange={(e: React.ChangeEvent<HTMLInputElement>) => {
                handleChangeValue("document_name", e.target.value);
              }}
              disabled={!isCanEdit}
              helperText={
                errorMessage.includes("document_name")
                  ? "Document Name is required."
                  : ""
              }
              required
            />
          </Grid>

          <Grid
            item
            lg={12}
            md={12}
            sm={12}
            xs={12}
            sx={{
              alignSelf: "end",
            }}
          >
            {action === "update" ? (
              handleDeleted ? (
                <UploadFile
                  fileAttachmentId={data.id_advisor_file}
                  fileAttachmentName={data.advisor_file_name}
                  noClick={data.id_advisor_file ?? true}
                  onDrop={(file) => {
                    setFile(file);
                  }}
                  onDeleteFile={() => {
                    setFile(null);
                  }}
                  formatText={t("FILE_UPLOAD.FILE_SIZE")}
                  disableDeleteFileId={!isCanEdit}
                  onDeleteFileId={() => {
                    setHandleDeleted(false);
                  }}
                  hiddenDownloadFile={isCanEdit}
                  {...(!isCanEdit
                    ? {
                        onDownloadFile: () => {
                          if (data.id_advisor_file) {
                            handleDownloadFile(
                              +data.id_advisor_file,
                              data.advisor_file_name
                            );
                          }
                        },
                      }
                    : {})}
                />
              ) : (
                <UploadFile
                  onDrop={(file) => {
                    setFile(file);
                  }}
                  file={file}
                  onDeleteFile={() => {
                    setFile(null);
                  }}
                  noClick={false}
                  // customFormat={["image/jpeg", "image/jpg", "image/png"]}
                  helperText={
                    errorMessage.includes("file") ? "File is required." : ""
                  }
                  required
                />
              )
            ) : (
              <UploadFile
                onDrop={(file) => {
                  setFile(file);
                }}
                file={file}
                onDeleteFile={() => {
                  setFile(null);
                }}
                noClick={false}
                // customFormat={["image/jpeg", "image/jpg", "image/png"]}
                helperText={
                  errorMessage.includes("file") ? "File is required." : ""
                }
                required
              />
            )}
          </Grid>

          <Grid
            item
            lg={12}
            md={12}
            sm={12}
            xs={12}
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <ButtonOutlined
              onClick={() => {
                setIsOpenModal(false);
                if (action !== "create") {
                  setIsCanEdit(false);
                }
              }}
              mode="del"
              buttonName="ยกเลิก"
              btnProps={{
                width: "125px",
                margin: "12px",
              }}
            />

            {isCanEdit && (
              <ButtonPrimary
                onClick={() => {
                  if (action === "create") {
                    handleSave(
                      file ? file[0] : undefined,
                      formDocument.document_name
                    );
                  } else if (action === "update") {
                    handleUpdate(
                      data ? data.id_advisor_file : null,
                      file ? file[0] : undefined,
                      formDocument.document_name,
                      data ? data.status : "active"
                    );
                  } else {
                    alertError("บันทึกเอกสาร ไม่สำเร็จ");
                  }
                }}
                buttonName={"บันทึก"}
                btnProps={{
                  width: "125px",
                  margin: "12px",
                }}
                mode="next"
              />
            )}

            {!isCanEdit && (
              <ButtonPrimary
                onClick={() => {
                  setIsCanEdit(true);
                }}
                buttonName={"แก้ไข"}
                btnProps={{
                  width: "125px",
                  margin: "12px",
                }}
              />
            )}

            {!isCanEdit && (
              <ButtonPrimary
                onClick={() => {
                  handleDelete(data ? data.id_advisor_file : null);
                }}
                mode="del"
                buttonName={"ลบ"}
                btnProps={{
                  width: "125px",
                  margin: "12px",
                }}
              />
            )}
          </Grid>
        </Grid>
      }
    />
  );
};

export default ModalFormDocument;
